import { useQuery } from '@tanstack/vue-query'
import type { MaybeRefOrGetter } from 'vue'
import { computed, reactive, ref, toValue, watch } from 'vue'
import { DEFAULT_PAGE_SIZE } from '@/modules/Core/constants/pagination'
import { REMINDERS_QUERY_KEY } from '@/modules/Reminder/requests/reminderQueryKeys'
import { fetchRemindersPaginated } from '@/modules/Reminder/requests/queries/useRemindersPaginatedQuery'
import type { Reminder } from '@/modules/Reminder/types/model'
import {
  type DateRange,
  getTodayKey,
  toDateKey,
} from '@/modules/Reminder/utils/jalaliCalendar'
import { reminderListItemToReminder } from '@/modules/Reminder/utils/reminderApiMappers'

// Overrides from create/update in this session until the list query refetches.
const descriptionById = reactive(new Map<number, string>())

async function fetchAllRemindersInRange({
  from,
  to,
}: DateRange): Promise<Reminder[]> {
  const reminders: Reminder[] = []

  for (let pageIndex = 1; ; pageIndex += 1) {
    const params = { pageIndex, pageSize: DEFAULT_PAGE_SIZE, from, to }
    const page = await fetchRemindersPaginated(params).then((data) => ({
      totalCount: data.totalCount,
      items: data.items.map((item) => reminderListItemToReminder(item)),
    }))
    reminders.push(...page.items)

    if (
      page.items.length === 0 ||
      pageIndex * DEFAULT_PAGE_SIZE >= page.totalCount
    ) {
      return reminders
    }
  }
}

/**
 * Fetches every reminder in `range` with a single query, so the calendar
 * markers and the list share one request. `dateKey` narrows the list to one
 * day; when it is null the list shows reminders from today onward.
 */
export function useRemindersList(
  range: MaybeRefOrGetter<DateRange>,
  dateKey: MaybeRefOrGetter<string | null> = null
) {
  const pageIndex = ref(1)

  const { data, isFetching } = useQuery({
    queryKey: computed(() => [
      REMINDERS_QUERY_KEY,
      toValue(range).from,
      toValue(range).to,
    ]),
    queryFn: () => fetchAllRemindersInRange(toValue(range)),
  })

  const allReminders = computed(() =>
    (data.value ?? []).map((reminder) => ({
      ...reminder,
      description: descriptionById.get(reminder.id) ?? reminder.description,
    }))
  )

  const markedDateKeys = computed(
    () =>
      new Set(allReminders.value.map((reminder) => toDateKey(reminder.date)))
  )

  const filteredReminders = computed(() => {
    const selectedKey = toValue(dateKey)
    const todayKey = getTodayKey()

    return allReminders.value.filter((reminder) => {
      const key = toDateKey(reminder.date)
      return selectedKey ? key === selectedKey : key >= todayKey
    })
  })

  const totalCount = computed(() => filteredReminders.value.length)

  const totalPages = computed(() =>
    Math.max(1, Math.ceil(totalCount.value / DEFAULT_PAGE_SIZE))
  )

  const reminders = computed(() =>
    filteredReminders.value.slice(
      (pageIndex.value - 1) * DEFAULT_PAGE_SIZE,
      pageIndex.value * DEFAULT_PAGE_SIZE
    )
  )

  watch(
    () => [toValue(range).from, toValue(range).to, toValue(dateKey)],
    () => {
      pageIndex.value = 1
    }
  )

  watch(totalPages, (pages) => {
    if (pageIndex.value > pages) {
      pageIndex.value = pages
    }
  })

  function rememberDescription(id: number, description: string) {
    descriptionById.set(id, description)
  }

  return {
    reminders,
    markedDateKeys,
    totalCount,
    totalPages,
    pageIndex,
    isFetching,
    rememberDescription,
  }
}
